import { useTracker } from '@archipro-website/tracker';
import { RoccoLink } from '@rocco/components/link';
import {
    DetailSaveButton,
    GetInTouchRowContent,
    NumberedAccordion,
    useEnquiryFunnelStages,
    type GetInTouchProfessional,
    type NumberedAccordionRow,
} from '~/ui-rocco/pages/detail-shared';
import { CategoryPills } from '~/ui-rocco/pages/home-page/components/CategoryPills';
import type {
    RevampCredit,
    RevampDownload,
    RevampHeaderCategory,
} from '../type';
import { trackArticleViewProfileClick } from '~/modules/tracking/util/trackArticleProfessionalCta';
import { RevampDownloadRow } from './RevampDownloadRow';
import { RevampShareLinks } from './RevampShareLinks';

interface RevampThemesCreditsProps {
    themes: RevampHeaderCategory[];
    credits: RevampCredit[];
    professional?: { title: string; link: string };
    /** The article's guide files; the row is omitted when there are none. */
    downloads: RevampDownload[];
    /** Article the download event is attributed to. */
    articleID: number;
    /** Article title, sent with the enquiry for tracking. */
    articleTitle: string;
    /** The article's own path, shared by the social buttons. */
    shareLink: string;
    /** Full professional behind the inline enquiry row. */
    enquiryProfessional?: GetInTouchProfessional;
}

// "Themes" / "Credits" labels — Figma shows SF Pro Semibold; font-sans has no
// 600 face so use the real 500 cut, 15px, tracking -0.05em. The design's
// #9a9a9a only reaches 2.8:1 on white, so this uses #767676 — the darkest grey
// that still reads as muted at the 4.5:1 body-text threshold (WCAG AA).
const labelClass =
    'm-0 font-sans font-medium tracking-[-0.05em] text-[#767676] md:text-[0.9375rem]';

/**
 * Themes & Credits block (Figma 2479:72290): theme pills beside the credited
 * names, the article's share buttons at the right, then the numbered CTA
 * accordion rows. Columns hide when empty.
 */
export const RevampThemesCredits = ({
    themes,
    credits,
    professional,
    downloads,
    articleID,
    articleTitle,
    shareLink,
    enquiryProfessional,
}: RevampThemesCreditsProps) => {
    const tracker = useTracker();
    // Owned here rather than in the form, which the accordion unmounts when the
    // row closes; the hook resets it per page view.
    const firedFunnelStages = useEnquiryFunnelStages('article', articleID);
    const rows: NumberedAccordionRow[] = [];
    if (downloads.length > 0) {
        rows.push({
            title: 'Downloads',
            content: (
                <RevampDownloadRow
                    downloads={downloads}
                    articleID={articleID}
                    professionalTitle={professional?.title}
                />
            ),
        });
    }
    if (professional && enquiryProfessional) {
        rows.push({
            title: `Contact ${professional.title}`,
            content: (
                <GetInTouchRowContent
                    professional={enquiryProfessional}
                    itemType="article"
                    siteTreeID={articleID}
                    itemTitle={articleTitle}
                    itemDescription="this article"
                    firedFunnelStages={firedFunnelStages}
                    theme="light"
                />
            ),
        });
    }

    if (themes.length === 0 && credits.length === 0 && rows.length === 0) {
        return null;
    }

    return (
        <section className="w-full bg-white px-4.5 md:px-[3rem]">
            <div className="flex flex-wrap gap-10 text-[#181818] md:gap-[7.3125rem]">
                {themes.length > 0 && (
                    <div className="flex flex-col gap-3 md:w-[19.875rem] md:gap-[1.5625rem]">
                        <p className={labelClass}>Themes</p>
                        <CategoryPills
                            items={themes.map((theme) => ({
                                label: theme.title,
                                to: theme.link,
                            }))}
                            // Figma 2497:77394 shows filled grey pills here, not
                            // the home page's outline style.
                            pillClassName="border-0 bg-[#ebebeb] text-[#181818]"
                        />
                    </div>
                )}
                {credits.length > 0 && (
                    <div className="flex flex-col gap-3 md:w-[19.875rem] md:gap-[1.5625rem]">
                        <p className={labelClass}>Credits</p>
                        <ul className="m-0 flex list-none flex-col gap-[0.1875rem] p-0">
                            {credits.map((credit) => (
                                <li key={credit.name}>
                                    {credit.link ? (
                                        <RoccoLink
                                            to={credit.link}
                                            // Only the professional's credit
                                            // carries a link, so this is a
                                            // profile visit (BC-4876).
                                            onClick={() =>
                                                trackArticleViewProfileClick(
                                                    tracker,
                                                    'credits',
                                                )
                                            }
                                            // Underlined so the one linked
                                            // credit reads as a link against
                                            // the unlinked names beside it.
                                            className="font-sans leading-normal text-[#333] uppercase underline underline-offset-2 transition-opacity hover:opacity-70 max-md:text-xs md:text-[0.75rem]"
                                        >
                                            {credit.name}
                                        </RoccoLink>
                                    ) : (
                                        <span className="font-sans leading-normal text-[#333] uppercase max-md:text-xs md:text-[0.75rem]">
                                            {credit.name}
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                <div className="ml-auto flex items-center gap-[0.75rem] self-center">
                    <RevampShareLinks
                        shareLink={shareLink}
                        articleID={articleID}
                        articleTitle={articleTitle}
                    />
                    {/* Save the article to a design board — the revamp's
                        equivalent of the legacy header's Share + Save pair.
                        Sized down to sit with the social icons rather than at
                        the 52px detail-CTA height. */}
                    <DetailSaveButton
                        pinId={articleID}
                        pinType="ArticlePin"
                        className="size-[2.125rem]! border-solid"
                    />
                </div>
            </div>
            {rows.length > 0 && (
                <NumberedAccordion
                    rows={rows}
                    theme="light"
                    // A top-level section of the article, directly under its
                    // h1 — Rocco's default h3 would skip a level here.
                    headingLevel={2}
                    // This section already carries the article gutters, so opt
                    // out of the accordion's default detail-page padding.
                    className="mt-8 max-md:px-0 md:mt-[3rem] md:px-0"
                />
            )}
        </section>
    );
};
